import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Star, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, dateShort, productImage } from "@/lib/api";
import { PageHeader, EmptyState, Field, Thumb } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime";
const selectClass = "rounded-sm border border-line bg-white px-3 py-2.5 text-sm text-stone outline-none focus:border-lime";

export default function Library() {
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [view, setView] = useState("grid");
  const [filters, setFilters] = useState({ q: "", category: "", vendor_id: "", brand: "", min_price: "", max_price: "", favorites: false, previously_used: false });
  const [copyState, setCopyState] = useState({ open: false, product: null, project_id: "", space_id: "" });
  const [spaces, setSpaces] = useState([]);

  const load = () => {
    const params = { library: true };
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false) return;
      params[k] = v;
    });
    api.get("/products", { params }).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  };

  useEffect(() => {
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/meta/enums").then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (copyState.project_id) api.get(`/projects/${copyState.project_id}/spaces`).then((r) => setSpaces(r.data)).catch(() => setSpaces([]));
  }, [copyState.project_id]);

  const brands = useMemo(() => [...new Set(products.map((p) => p.brand).filter(Boolean))], [products]);

  const favorite = async (p) => {
    try {
      const { data } = await api.post(`/products/${p.id}/favorite`);
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, is_favorite: data.is_favorite } : x)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copy = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/products/${copyState.product.id}/copy-to-project`, {
        project_id: copyState.project_id, space_id: copyState.space_id || null,
      });
      toast.success("Added to project");
      setCopyState({ open: false, product: null, project_id: "", space_id: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-10" data-testid="library-page">
      <PageHeader
        overline="Sourcing"
        title="Products / Library"
        subtitle="Reusable specifications that can be added into any project without touching historical records."
        actions={
          <>
            <div className="flex rounded-sm border border-line bg-white">
              <button data-testid="library-grid-view-btn" onClick={() => setView("grid")} className={`p-2.5 transition-colors ${view === "grid" ? "bg-[#EDEFC4] text-ink" : "text-stone hover:text-ink"}`} aria-label="Grid view">
                <LayoutGrid className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <button data-testid="library-table-view-btn" onClick={() => setView("table")} className={`p-2.5 transition-colors ${view === "table" ? "bg-[#EDEFC4] text-ink" : "text-stone hover:text-ink"}`} aria-label="Table view">
                <List className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <Link data-testid="library-add-product-btn" to="/products/new?mode=link" className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
              <Plus className="h-4 w-4" strokeWidth={1.5} /> Add Product
            </Link>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" strokeWidth={1.5} />
          <input data-testid="library-search-input" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search library or SKU" className="w-full rounded-sm border border-line bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-lime" />
        </div>
        <select data-testid="library-category-filter" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className={selectClass}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select data-testid="library-vendor-filter" value={filters.vendor_id} onChange={(e) => setFilters({ ...filters, vendor_id: e.target.value })} className={selectClass}>
          <option value="">All vendors</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select data-testid="library-brand-filter" value={filters.brand} onChange={(e) => setFilters({ ...filters, brand: e.target.value })} className={selectClass}>
          <option value="">All brands</option>
          {brands.map((b) => <option key={b}>{b}</option>)}
        </select>
        <input data-testid="library-min-price" type="number" placeholder="Min $" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} className={`num w-24 ${selectClass}`} />
        <input data-testid="library-max-price" type="number" placeholder="Max $" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} className={`num w-24 ${selectClass}`} />
        <button data-testid="library-favorites-filter" onClick={() => setFilters({ ...filters, favorites: !filters.favorites })} className={`rounded-sm border px-3 py-2 text-xs transition-colors ${filters.favorites ? "border-lime bg-lime text-ink" : "border-line bg-white text-stone hover:border-fog"}`}>
          Favorites
        </button>
        <button data-testid="library-used-filter" onClick={() => setFilters({ ...filters, previously_used: !filters.previously_used })} className={`rounded-sm border px-3 py-2 text-xs transition-colors ${filters.previously_used ? "border-lime bg-lime text-ink" : "border-line bg-white text-stone hover:border-fog"}`}>
          Previously Used
        </button>
      </div>

      {products.length === 0 ? (
        <EmptyState testId="library-empty" title="No library products match" body="Save any project product to the library to reuse it later." />
      ) : view === "grid" ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="library-grid">
          {products.map((p, i) => (
            <div key={p.id} data-testid={`library-card-${p.id}`} className="card-surface card-hover animate-rise overflow-hidden" style={{ animationDelay: `${i * 40}ms` }}>
              <Link to={`/products/${p.id}`} className="block aspect-square overflow-hidden bg-line">
                {productImage(p) ? <img src={productImage(p)} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.04]" /> : <Thumb className="h-full w-full" rounded="rounded-none" />}
              </Link>
              <div className="space-y-3 p-5">
                <div className="flex items-start justify-between gap-3">
                  <span className="overline">{p.category}</span>
                  <button data-testid={`library-favorite-${p.id}`} onClick={() => favorite(p)} aria-label="Favorite">
                    <Star className={`h-4 w-4 transition-colors ${p.is_favorite ? "fill-[#BEC221] text-lime-dark" : "text-fog hover:text-ink"}`} strokeWidth={1.5} />
                  </button>
                </div>
                <Link to={`/products/${p.id}`} className="block font-serif text-lg leading-snug text-ink hover:text-lime-dark">{p.name}</Link>
                <div className="num text-sm text-ink">{money(p.retail_price)}</div>
                <div className="text-xs text-fog">{p.vendor_name || p.brand || "—"}</div>
                {!!p.used_in_projects?.length && <div className="text-[11px] text-fog">Used in {p.used_in_projects.join(", ")}</div>}
                <div className="text-[11px] text-fog">Updated {dateShort(p.updated_at || p.created_at)}</div>
                <button
                  data-testid={`library-copy-btn-${p.id}`}
                  onClick={() => setCopyState({ open: true, product: p, project_id: "", space_id: "" })}
                  className="w-full rounded-sm border border-line px-3 py-2 text-xs text-ink transition-colors hover:border-lime hover:bg-[#EDEFC4]"
                >
                  Add to project
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card-surface overflow-x-auto scroll-thin" data-testid="library-table">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                {["", "Product", "Vendor", "Category", "Reference Price", "Used In", "Last Updated", ""].map((h, i) => (
                  <th key={i} className="overline px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-line last:border-0 hover:bg-bone">
                  <td className="px-4 py-3"><Link to={`/products/${p.id}`}><Thumb src={productImage(p)} className="h-11 w-14" /></Link></td>
                  <td className="px-4 py-3"><Link to={`/products/${p.id}`} className="text-ink hover:text-lime-dark">{p.name}</Link></td>
                  <td className="px-4 py-3 text-stone">{p.vendor_name || p.brand || "—"}</td>
                  <td className="px-4 py-3 text-stone">{p.category}</td>
                  <td className="num px-4 py-3 text-right">{money(p.retail_price)}</td>
                  <td className="px-4 py-3 text-xs text-stone">{p.used_in_projects?.join(", ") || "—"}</td>
                  <td className="num px-4 py-3 text-xs text-stone">{dateShort(p.updated_at || p.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button data-testid={`library-favorite-row-${p.id}`} onClick={() => favorite(p)} aria-label="Favorite">
                        <Star className={`h-4 w-4 ${p.is_favorite ? "fill-[#BEC221] text-lime-dark" : "text-fog"}`} strokeWidth={1.5} />
                      </button>
                      <button data-testid={`library-copy-row-${p.id}`} onClick={() => setCopyState({ open: true, product: p, project_id: "", space_id: "" })} className="rounded-sm border border-line px-3 py-1.5 text-xs text-ink hover:border-lime">
                        Add to project
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {copyState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5" data-testid="library-copy-modal">
          <div className="absolute inset-0 bg-ink/30 backdrop-blur-sm" onClick={() => setCopyState({ open: false, product: null, project_id: "", space_id: "" })} />
          <form onSubmit={copy} className="card-surface relative w-full max-w-md space-y-5 p-7">
            <h3 className="font-serif text-2xl text-ink">Add “{copyState.product?.name}”</h3>
            <Field label="Project">
              <select data-testid="copy-project-select" required value={copyState.project_id} onChange={(e) => setCopyState((s) => ({ ...s, project_id: e.target.value }))} className={input}>
                <option value="">Select project</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Space">
              <select data-testid="copy-space-select" value={copyState.space_id} onChange={(e) => setCopyState((s) => ({ ...s, space_id: e.target.value }))} className={input}>
                <option value="">— Unassigned —</option>
                {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <div className="flex gap-3">
              <button data-testid="copy-submit-btn" type="submit" className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Add to Project</button>
              <button type="button" onClick={() => setCopyState({ open: false, product: null, project_id: "", space_id: "" })} className="rounded-sm border border-line px-4 py-2.5 text-sm text-ink hover:bg-line/50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
